import * as React from "react";

export type SortDir = "asc" | "desc";

export interface SortableDataResult<Row, Key extends string> {
  sortKey: Key;
  sortDir: SortDir;
  /** Returns true when a header for this column is currently the active sort. */
  isActive: (key: Key) => boolean;
  /** Toggles the sort direction for `key`, or switches sort key. */
  toggle: (key: Key, defaultDesc?: boolean) => void;
  /** Renders the sort indicator arrow for the given key, or "". */
  arrow: (key: Key) => "↓" | "↑" | "";
  sorted: Row[];
}

/**
 * Hook variant for tables that need bespoke row chrome (expansion rows,
 * leading custom cells, etc.) but want to share sort logic with the table
 * engine. Consumers wire up their own header chrome and render `sorted`.
 *
 * Extracted from the old `sortable-table.tsx` when SortableTable folded into
 * `DataTable` (one table engine) — kept as a standalone hook for the few
 * bespoke consumers that drive their own markup.
 */
export function useSortableData<Row, Key extends string>(
  rows: Row[],
  comparators: Partial<Record<Key, (a: Row, b: Row) => number>>,
  initial: { key: Key; dir: SortDir },
): SortableDataResult<Row, Key> {
  const [sortKey, setSortKey] = React.useState<Key>(initial.key);
  const [sortDir, setSortDir] = React.useState<SortDir>(initial.dir);

  const sorted = React.useMemo(() => {
    const cmp = comparators[sortKey];
    if (!cmp) return rows;
    const dir = sortDir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => cmp(a, b) * dir);
  }, [rows, comparators, sortKey, sortDir]);

  return {
    sortKey,
    sortDir,
    isActive: (k) => k === sortKey,
    arrow: (k) => (k === sortKey ? (sortDir === "desc" ? "↓" : "↑") : ""),
    toggle: (k, defaultDesc) => {
      if (k === sortKey) {
        setSortDir((d) => (d === "desc" ? "asc" : "desc"));
      } else {
        setSortKey(k);
        setSortDir(defaultDesc ? "desc" : "asc");
      }
    },
    sorted,
  };
}
